import React, { useEffect } from 'react';
import './StarTrail.css';

/**
 * StarTrail Component
 * 
 * To use it, simply place it inside the component or section where you want the effect.
 * If you want it restricted to a specific container, pass the ref of that container as `containerRef`.
 * Otherwise, it will listen to window mousemove and append stars to document.body.
 */
const StarTrail = ({ containerRef }) => {
  useEffect(() => {
    const target = containerRef?.current || window;
    let x1 = 0, y1 = 0;
    const dist_to_draw = 50;
    const delay = 1000;
    const fsize = ['1.1rem', '1.4rem', '.8rem', '1.7rem'];
    const colors = [
      '#f69ee3ff',
      '#7dccecff',
      '#afa5f1ff',
      '#eae2f7ff',
      '#77bae6ff'
    ];

    const rand = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;
    const selRand = (o) => o[rand(0, o.length - 1)];
    const distanceTo = (x1, y1, x2, y2) => Math.sqrt(Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2));
    const shouldDraw = (x, y) => distanceTo(x1, y1, x, y) >= dist_to_draw;

    const addStr = (x, y, clientX, clientY) => {
      const str = document.createElement("div");
      str.innerHTML = '&#10022;';
      str.className = 'star-trail-element';
      
      // Position calculation
      str.style.top = `${y + rand(-20, 20)}px`;
      str.style.left = `${x}px`;
      str.style.color = selRand(colors);
      str.style.fontSize = selRand(fsize);
      
      const parent = containerRef?.current || document.body;
      
      // If we are appending to a container, ensure it has relative positioning
      if (containerRef?.current && getComputedStyle(parent).position === 'static') {
        parent.style.position = 'relative';
      }
      
      parent.appendChild(str);

      const vh = window.innerHeight;
      const fs = 10 + 5 * parseFloat(getComputedStyle(str).fontSize);
      
      str.animate({
        translate: `0 ${(clientY + fs) > vh ? vh - clientY : fs}px`,
        opacity: 0,
        transform: `rotateX(${rand(1, 500)}deg) rotateY(${rand(1, 500)}deg)`
      }, {
        duration: delay,
        fill: 'forwards',
      });

      setTimeout(() => {
        str.remove();
      }, delay);
    };

    const handleMouseMove = (e) => {
      const { clientX, clientY } = e;
      
      let x, y;
      if (containerRef?.current) {
        const rect = containerRef.current.getBoundingClientRect();
        x = clientX - rect.left;
        y = clientY - rect.top;
      } else {
        x = clientX + window.scrollX;
        y = clientY + window.scrollY;
      }

      if (shouldDraw(clientX, clientY)) {
        addStr(x, y, clientX, clientY);
        x1 = clientX;
        y1 = clientY;
      }
    };

    target.addEventListener("mousemove", handleMouseMove);
    return () => target.removeEventListener("mousemove", handleMouseMove);
  }, [containerRef]);

  return null;
};

export default StarTrail;
